/**
 * ============================================================================
 * VEDANG JYOTISHAM V6.5 — BIRTH TIME RECTIFICATION (BTR) P1 PHASE 3
 * ASTROLOGICAL PLATEAU & STRUCTURAL INVARIANT ENGINE
 * ============================================================================
 * Specification: VJ-BTR-V65-SPEC-001 v3.0 §7
 * Implementation Plan: VJ-BTR-V65-PLAN-001 v1.0
 * Authority: Formal Human Authorization dated 2026-10-13 (AOS V1 Stage 7)
 *
 * Core Capabilities:
 * 1. Extraction of the 6-factor composite invariant signature:
 *    (D1 Lagna sign, D9 Navamsha sign, D10 Dashamsha sign,
 *     KP House 1 Cusp sub-lord, KP House 7 Cusp sub-lord, KP House 10 Cusp sub-lord)
 * 2. Strict half-open interval semantics [t_start, t_end) with <= 1s bisection precision.
 * 3. Four-tier epistemic taxonomy:
 *    - DENSE_VERIFIED (100% 1-second ticks verified uniform)
 *    - SAMPLED_CONSISTENT (discrete sample agreement with sampling uncertainty disclosure)
 *    - UNRESOLVED (bisection non-convergence, cuspal edge oscillation)
 * 4. Empirical dynamic adaptive sampling heuristic for fast-rising signs & high latitudes.
 * 5. Full SearchCoverageAudit ledger accounting with fail-closed circuit breakers.
 * ============================================================================
 */

import * as _Astronomy from 'astronomy-engine';
const Astronomy: any = (_Astronomy as any).MakeTime ? _Astronomy : ((_Astronomy as any).default || _Astronomy);

import {
  ZodiacSign,
  PlanetName
} from '../types/kundali';
import {
  ZODIAC_SIGNS,
  calculateAscendant,
  getLahiriAyanamsha
} from './vedicEngine';
import {
  getKPLordsForLongitude
} from './kpEngine';
import {
  AstrologicalPlateauInterval,
  CandidateTuple,
  SearchCoverageAudit,
  SearchCoverageStatus,
  SearchOutcomeStatus,
  TemporalRegion
} from '../types/btr';
import {
  resolveLocalToUtc,
  formatUtcToLocal
} from './btrTimezoneResolver';
import {
  computeSearchCoverageStatus,
  validateSearchCoverageAudit,
  isValidCalendarDate
} from './btrContractValidation';

// ============================================================================
// 1. TYPE DEFINITIONS & CONTRACT EXTENSIONS
// ============================================================================

export type PlateauInvariants = AstrologicalPlateauInterval['invariantsHeld'];

export type PlateauVerificationLevel = 'DENSE_VERIFIED' | 'SAMPLED_CONSISTENT' | 'UNRESOLVED';

export interface VerifiedPlateauInterval extends AstrologicalPlateauInterval {
  /** Epistemic verification rigor of the interval */
  verificationLevel: PlateauVerificationLevel;
  /** ISO UTC start timestamp (inclusive) */
  startUtcInstant: string;
  /** ISO UTC end timestamp (exclusive) */
  endUtcInstant: string;
}

export interface PlateauScanParams {
  startUtc: string;
  endUtc: string;
  latitude: number;
  longitude: number;
  timezoneId: string;
  placeName?: string;
  maxEvaluations?: number; // Provisional benchmark target: 2500
  maxDurationMs?: number;  // Provisional benchmark target: 400ms
  customStepSeconds?: number;
  denseVerify?: boolean;
}

export interface PlateauCoverageAudit extends SearchCoverageAudit {
  samplingUncertainty: boolean;
  minimumResolvablePlateauSeconds: number;
}

export interface PlateauScanResult {
  plateaus: VerifiedPlateauInterval[];
  coverageAudit: PlateauCoverageAudit;
  outcomeStatus: SearchOutcomeStatus;
  evaluationCount: number;
  durationMs: number;
}

export interface BisectionResult {
  boundaryUtc: string;
  iterations: number;
  converged: boolean;
  reason?: string;
  evaluationsExecuted: number;
}

export interface VelocityEstimate {
  ascVelocityDegPerSec: number;
  mcVelocityDegPerSec: number;
  recommendedStepSeconds: number;
}

// Canonical engine version for provenance
export const BTR_PLATEAU_ENGINE_VERSION = '6.5.0-P3';

// ============================================================================
// 2. CANONICAL SHASHTRIC & ASTRONOMICAL SIGN CALCULATIONS
// ============================================================================

/**
 * Calculates BPHS Navamsha (D9) sign index (0-11) for a sidereal longitude.
 * Element starts: Fire (0,4,8)->0, Earth (1,5,9)->9, Air (2,6,10)->6, Water (3,7,11)->3.
 */
export function getNavamshaSignIndex(lon: number): number {
  const norm = ((lon % 360) + 360) % 360;
  const signIdx = Math.floor(norm / 30);
  const degInSign = norm % 30;
  const navPart = Math.floor(degInSign / (30 / 9)); // 0 to 8
  const element = signIdx % 4;
  const startSigns = [0, 9, 6, 3];
  return (startSigns[element] + navPart) % 12;
}

/**
 * Calculates BPHS Dashamsha (D10) sign index (0-11) for a sidereal longitude.
 * Odd signs start from self; Even signs start from 9th sign (signIdx + 8) % 12 (BPHS Ch. 6, v. 22).
 */
export function getDashamshaSignIndex(lon: number): number {
  const norm = ((lon % 360) + 360) % 360;
  const signIdx = Math.floor(norm / 30);
  const degInSign = norm % 30;
  const part = Math.floor(degInSign / 3); // 0 to 9
  const isOdd = signIdx % 2 === 0; // 0=Aries (odd)
  const start = isOdd ? signIdx : (signIdx + 8) % 12;
  return (start + part) % 12;
}

/**
 * Calculates Sidereal Midheaven (MC / House 10 Cusp) for an instant.
 * Derived from Local Apparent Sidereal Time (RAMC) and true obliquity of date.
 */
export function calculateMidheavenLongitude(date: Date, lon: number, ayanamsha: number): number {
  const time = Astronomy.MakeTime(date);
  const siderealTime = Astronomy.SiderealTime(time); // Greenwich apparent sidereal time in hours
  const lstHours = (siderealTime + lon / 15.0 + 24.0) % 24.0;
  const lstDeg = lstHours * 15.0;
  const rad = Math.PI / 180;
  const tilt = Astronomy.e_tilt(time);
  const eps = ((tilt && typeof tilt.tobl === 'number') ? tilt.tobl : 23.4392911) * rad;
  const theta = lstDeg * rad;

  // Tropical MC: tan(MC) = sin(theta) / (cos(theta) * cos(eps))
  const mcTropRad = Math.atan2(Math.sin(theta), Math.cos(theta) * Math.cos(eps));
  const mcTropDeg = (mcTropRad / rad + 360) % 360;
  return ((mcTropDeg - ayanamsha) % 360 + 360) % 360;
}

// ============================================================================
// 3. STRUCTURAL INVARIANT SIGNATURE EXTRACTION
// ============================================================================

/**
 * Evaluates the 6-factor composite invariant signature at a specific UTC instant.
 * @throws Error on invalid coordinates or calculation singularities.
 */
export function getStructuralInvariantsAtInstant(
  utcInstant: string | Date,
  lat: number,
  lon: number
): PlateauInvariants {
  if (typeof lat !== 'number' || isNaN(lat) || Math.abs(lat) >= 90) {
    throw new Error(`Invalid latitude "${lat}": must be a finite number strictly between -90 and +90`);
  }
  if (typeof lon !== 'number' || isNaN(lon) || Math.abs(lon) > 180) {
    throw new Error(`Invalid longitude "${lon}": must be a finite number between -180 and +180`);
  }

  const date = typeof utcInstant === 'string' ? new Date(utcInstant) : utcInstant;
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid UTC instant "${utcInstant}"`);
  }

  const astroTime = Astronomy.MakeTime(date);
  const ayanamsha = getLahiriAyanamsha(astroTime);

  // 1. Sidereal Ascendant degree (Cusp 1)
  const ascDeg = calculateAscendant(date, lat, lon, ayanamsha);
  if (typeof ascDeg !== 'number' || isNaN(ascDeg)) {
    throw new Error(`Ascendant calculation singularity encountered at lat=${lat}, lon=${lon}, instant=${date.toISOString()}`);
  }
  const normAsc = ((ascDeg % 360) + 360) % 360;

  // 2. D1 Ascendant Sign
  const d1SignIdx = Math.floor(normAsc / 30);
  const d1AscendantSign = ZODIAC_SIGNS[d1SignIdx % 12];

  // 3. D9 Navamsha Sign
  const d9SignIdx = getNavamshaSignIndex(normAsc);
  const d9NavamshaSign = ZODIAC_SIGNS[d9SignIdx];

  // 4. D10 Dashamsha Sign
  const d10SignIdx = getDashamshaSignIndex(normAsc);
  const d10DashamshaSign = ZODIAC_SIGNS[d10SignIdx];

  // 5. KP Cusp 1 Sub-Lord (Ascendant)
  const kp1 = getKPLordsForLongitude(normAsc);
  const kpCuspSubLordHouse1 = kp1.subLord;

  // 6. KP Cusp 7 Sub-Lord (Descendant = Ascendant + 180)
  const descDeg = ((normAsc + 180) % 360 + 360) % 360;
  const kp7 = getKPLordsForLongitude(descDeg);
  const kpCuspSubLordHouse7 = kp7.subLord;

  // 7. KP Cusp 10 Sub-Lord (Midheaven / MC)
  const mcDeg = calculateMidheavenLongitude(date, lon, ayanamsha);
  const kp10 = getKPLordsForLongitude(mcDeg);
  const kpCuspSubLordHouse10 = kp10.subLord;

  return {
    d1AscendantSign,
    d9NavamshaSign,
    d10DashamshaSign,
    kpCuspSubLordHouse1,
    kpCuspSubLordHouse7,
    kpCuspSubLordHouse10
  };
}

/**
 * Strict boolean equality check across all 6 invariant factors.
 */
export function areInvariantsEqual(a: PlateauInvariants, b: PlateauInvariants): boolean {
  return (
    a.d1AscendantSign === b.d1AscendantSign &&
    a.d9NavamshaSign === b.d9NavamshaSign &&
    a.d10DashamshaSign === b.d10DashamshaSign &&
    a.kpCuspSubLordHouse1 === b.kpCuspSubLordHouse1 &&
    a.kpCuspSubLordHouse7 === b.kpCuspSubLordHouse7 &&
    a.kpCuspSubLordHouse10 === b.kpCuspSubLordHouse10
  );
}

// ============================================================================
// 4. EMPIRICAL VELOCITY ESTIMATION & DYNAMIC ADAPTIVE SAMPLING
// ============================================================================

/**
 * Estimates instantaneous rising velocity via forward finite differences:
 * |lambda(t + 1s) - lambda(t)| / 1s.
 * Computes the recommended dynamic sampling step for fast-rising signs / high latitudes.
 * Labeled strictly as an empirical heuristic.
 */
export function estimateAscendantVelocity(
  utcInstant: string | Date,
  lat: number,
  lon: number
): VelocityEstimate {
  const d0 = typeof utcInstant === 'string' ? new Date(utcInstant) : utcInstant;
  const d1 = new Date(d0.getTime() + 1000); // 1-second forward tick

  const t0 = Astronomy.MakeTime(d0);
  const t1 = Astronomy.MakeTime(d1);
  const ayan0 = getLahiriAyanamsha(t0);
  const ayan1 = getLahiriAyanamsha(t1);

  const asc0 = calculateAscendant(d0, lat, lon, ayan0);
  const asc1 = calculateAscendant(d1, lat, lon, ayan1);
  const mc0 = calculateMidheavenLongitude(d0, lon, ayan0);
  const mc1 = calculateMidheavenLongitude(d1, lon, ayan1);

  // Shortest angular difference accounting for circular 360 wrap
  const diffAsc = Math.abs((((asc1 - asc0 + 540) % 360) - 180));
  const diffMC = Math.abs((((mc1 - mc0 + 540) % 360) - 180));

  const maxVel = Math.max(diffAsc, diffMC);
  const subMinDeg = 40 / 60; // Sun sub-lord arc = 40 arcminutes = 0.666667 deg

  let calculatedStep = 60;
  if (maxVel > 0.0001) {
    calculatedStep = Math.floor(subMinDeg / (maxVel * 1.5));
  }

  // Bounded between 5s minimum and 60s maximum
  const recommendedStepSeconds = Math.max(5, Math.min(60, calculatedStep));

  return {
    ascVelocityDegPerSec: diffAsc,
    mcVelocityDegPerSec: diffMC,
    recommendedStepSeconds
  };
}

// ============================================================================
// 5. BOOLEAN BISECTION & RECURSIVE BRACKET VALIDATION
// ============================================================================

/**
 * Bisects an interval [earlierUtc, laterUtc] where invariants differ to locate
 * the exact 1-second boundary instant t* where signature transitions.
 * Converges in <= 20 iterations (typically <= 6 iterations for 60s windows).
 */
export function bisectPlateauBoundary(
  earlierUtc: string,
  laterUtc: string,
  lat: number,
  lon: number,
  toleranceSeconds: number = 1,
  maxIterations: number = 20
): BisectionResult {
  const tA = Math.floor(new Date(earlierUtc).getTime() / 1000);
  const tB = Math.floor(new Date(laterUtc).getTime() / 1000);

  if (tB <= tA) {
    return {
      boundaryUtc: earlierUtc,
      iterations: 0,
      converged: true,
      evaluationsExecuted: 0
    };
  }

  const targetInvariants = getStructuralInvariantsAtInstant(new Date(tA * 1000).toISOString(), lat, lon);
  const laterInvariants = getStructuralInvariantsAtInstant(new Date(tB * 1000).toISOString(), lat, lon);

  if (areInvariantsEqual(targetInvariants, laterInvariants)) {
    return {
      boundaryUtc: new Date(tB * 1000).toISOString(),
      iterations: 0,
      converged: false,
      reason: 'IDENTICAL_ENDPOINTS',
      evaluationsExecuted: 2
    };
  }

  let iter = 0;
  let low = tA;
  let high = tB;

  while (high - low > toleranceSeconds && iter < maxIterations) {
    iter++;
    const mid = low + Math.floor((high - low) / 2);
    const midUtc = new Date(mid * 1000).toISOString();
    const midInvariants = getStructuralInvariantsAtInstant(midUtc, lat, lon);

    if (areInvariantsEqual(targetInvariants, midInvariants)) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const converged = (high - low) <= toleranceSeconds;
  // high is the first second where the signature has transitioned to the new state
  return {
    boundaryUtc: new Date(high * 1000).toISOString(),
    iterations: iter,
    converged,
    reason: converged ? undefined : 'MAX_ITERATIONS_EXCEEDED',
    evaluationsExecuted: 2 + iter
  };
}

/**
 * Verifies uniformity across an interval ticks.
 */
export function verifyIntervalUniformity(
  startUtc: string,
  endUtc: string,
  lat: number,
  lon: number,
  invariants: PlateauInvariants,
  stepSeconds: number = 1
): { isUniform: boolean; discrepancies: number; evaluatedSeconds: number } {
  const tStart = Math.floor(new Date(startUtc).getTime() / 1000);
  const tEnd = Math.floor(new Date(endUtc).getTime() / 1000);
  let discrepancies = 0;
  let evaluatedSeconds = 0;

  for (let t = tStart; t < tEnd; t += stepSeconds) {
    evaluatedSeconds++;
    const inv = getStructuralInvariantsAtInstant(new Date(t * 1000).toISOString(), lat, lon);
    if (!areInvariantsEqual(inv, invariants)) {
      discrepancies++;
    }
  }

  return {
    isUniform: discrepancies === 0,
    discrepancies,
    evaluatedSeconds
  };
}

// ============================================================================
// 6. CANDIDATE PLATEAU RESOLVER
// ============================================================================

/**
 * Resolves the continuous astrological plateau interval encompassing a CandidateTuple.
 * Preserves strict half-open interval semantics [t_start, t_end).
 */
export function resolvePlateauForCandidate(
  candidate: CandidateTuple,
  maxWindowMinutes: number = 60,
  denseVerify: boolean = false
): VerifiedPlateauInterval {
  const res = resolveLocalToUtc({
    localDate: candidate.localDate,
    localClockTime: candidate.localClockTime,
    timezoneId: candidate.timezoneId,
    latitude: candidate.latitude,
    longitude: candidate.longitude,
    placeName: candidate.placeName
  });

  if (res.status !== 'RESOLVED_EXACT') {
    throw new Error(`Cannot resolve candidate plateau: local time "${candidate.localClockTime}" on ${candidate.localDate} in timezone "${candidate.timezoneId}" has status "${res.status}"`);
  }

  const candidateUtcIso = res.resolvedCandidate?.utcInstant;
  if (!candidateUtcIso) {
    throw new Error(`Failed to resolve candidate time "${candidate.localClockTime}" to UTC: missing resolved candidate instant`);
  }
  const targetInvariants = getStructuralInvariantsAtInstant(candidateUtcIso, candidate.latitude, candidate.longitude);

  const candidateEpochSec = Math.floor(new Date(candidateUtcIso).getTime() / 1000);
  const maxSpanSec = maxWindowMinutes * 60;

  // 1. Backward boundary discovery (t_start)
  let backwardStep = 60;
  let currBack = candidateEpochSec;
  let prevBack = candidateEpochSec;

  while (currBack >= candidateEpochSec - maxSpanSec) {
    prevBack = currBack;
    currBack = Math.max(candidateEpochSec - maxSpanSec, currBack - backwardStep);
    const inv = getStructuralInvariantsAtInstant(new Date(currBack * 1000).toISOString(), candidate.latitude, candidate.longitude);
    if (!areInvariantsEqual(targetInvariants, inv)) {
      break;
    }
    if (currBack === candidateEpochSec - maxSpanSec) {
      break;
    }
  }

  let startEpochSec = candidateEpochSec - maxSpanSec;
  if (currBack < prevBack) {
    const bisectBack = bisectPlateauBoundary(
      new Date(currBack * 1000).toISOString(),
      new Date(prevBack * 1000).toISOString(),
      candidate.latitude,
      candidate.longitude
    );
    startEpochSec = Math.floor(new Date(bisectBack.boundaryUtc).getTime() / 1000);
  }

  // 2. Forward boundary discovery (t_end)
  let forwardStep = 60;
  let currFwd = candidateEpochSec;
  let prevFwd = candidateEpochSec;

  while (currFwd <= candidateEpochSec + maxSpanSec) {
    prevFwd = currFwd;
    currFwd = Math.min(candidateEpochSec + maxSpanSec, currFwd + forwardStep);
    const inv = getStructuralInvariantsAtInstant(new Date(currFwd * 1000).toISOString(), candidate.latitude, candidate.longitude);
    if (!areInvariantsEqual(targetInvariants, inv)) {
      break;
    }
    if (currFwd === candidateEpochSec + maxSpanSec) {
      break;
    }
  }

  let endEpochSec = candidateEpochSec + maxSpanSec;
  if (currFwd > prevFwd) {
    const bisectFwd = bisectPlateauBoundary(
      new Date(prevFwd * 1000).toISOString(),
      new Date(currFwd * 1000).toISOString(),
      candidate.latitude,
      candidate.longitude
    );
    endEpochSec = Math.floor(new Date(bisectFwd.boundaryUtc).getTime() / 1000);
  }

  const startMs = startEpochSec * 1000;
  const endMs = endEpochSec * 1000;
  const startUtcIso = new Date(startMs).toISOString();
  const endUtcIso = new Date(endMs).toISOString();
  const spanSeconds = Math.max(0, endEpochSec - startEpochSec);

  const startLocalObj = formatUtcToLocal(startMs, candidate.timezoneId);
  const endLocalObj = formatUtcToLocal(endMs, candidate.timezoneId);
  const startLocal = startLocalObj.localTime;
  const endLocal = endLocalObj.localTime;
  const localDate = startLocalObj.localDate;
  const intervalString = `${startLocal} - ${endLocal}`;

  // Mathematical reference midpoint (strictly for display)
  const midEpochSec = startEpochSec + Math.floor(spanSeconds / 2);
  const midMs = midEpochSec * 1000;
  const mathematicalMidpointReference = formatUtcToLocal(midMs, candidate.timezoneId).localTime;

  // Verification classification
  let verificationLevel: PlateauVerificationLevel = 'SAMPLED_CONSISTENT';
  if (denseVerify && spanSeconds > 0) {
    const unif = verifyIntervalUniformity(startUtcIso, endUtcIso, candidate.latitude, candidate.longitude, targetInvariants, 1);
    verificationLevel = unif.isUniform ? 'DENSE_VERIFIED' : 'UNRESOLVED';
  }

  return {
    startLocalTime: startLocal,
    endLocalTime: endLocal,
    localDate,
    intervalString,
    spanSeconds,
    mathematicalMidpointReference,
    invariantsHeld: targetInvariants,
    verificationLevel,
    startUtcInstant: startUtcIso,
    endUtcInstant: endUtcIso
  };
}

// ============================================================================
// 7. WINDOW SCANNER & COVERAGE ACCOUNTING LEDGER
// ============================================================================

/**
 * Scans a temporal window [startUtc, endUtc] discovering continuous plateau intervals.
 * Enforces evaluation budget limits, dynamic adaptive anti-aliasing, and SearchCoverageAudit.
 */
export function scanPlateausInWindow(params: PlateauScanParams): PlateauScanResult {
  const {
    startUtc,
    endUtc,
    latitude,
    longitude,
    timezoneId,
    maxEvaluations = 2500,
    maxDurationMs = 400,
    customStepSeconds,
    denseVerify = false
  } = params;

  const isoUtcRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
  if (!isoUtcRegex.test(startUtc) || !isoUtcRegex.test(endUtc)) {
    throw new Error(`Invalid UTC instant format: startUtc="${startUtc}", endUtc="${endUtc}". Must match ISO-8601 UTC format YYYY-MM-DDTHH:mm:ss[.sss]Z`);
  }

  const startDatePart = startUtc.split('T')[0];
  const endDatePart = endUtc.split('T')[0];
  if (!isValidCalendarDate(startDatePart) || !isValidCalendarDate(endDatePart)) {
    throw new Error(`Invalid calendar date in scan window: start="${startDatePart}", end="${endDatePart}"`);
  }

  const tStartMs = new Date(startUtc).getTime();
  const tEndMs = new Date(endUtc).getTime();

  if (isNaN(tStartMs) || isNaN(tEndMs) || tEndMs <= tStartMs) {
    throw new Error(`Invalid scan window: startUtc="${startUtc}", endUtc="${endUtc}"`);
  }

  const perfStart = performance.now();
  let evalCount = 0;

  const totalWindowSeconds = Math.round((tEndMs - tStartMs) / 1000);
  const plateaus: VerifiedPlateauInterval[] = [];
  const evaluatedRegions: TemporalRegion[] = [];
  const unevaluatedRegions: TemporalRegion[] = [];

  // Resolve timezone offset for local string formatting
  const tzRes = resolveLocalToUtc({
    localDate: startUtc.split('T')[0],
    localClockTime: '12:00:00',
    timezoneId,
    latitude,
    longitude
  });
  const offsetSec = tzRes.resolvedCandidate?.utcOffsetSeconds || 0;

  let currentEpochSec = Math.floor(tStartMs / 1000);
  const endEpochSec = Math.floor(tEndMs / 1000);

  let budgetExhausted = false;
  let samplingUncertainty = false;

  while (currentEpochSec < endEpochSec) {
    // 1. Budget Circuit Breaker
    const isTimeout = (performance.now() - perfStart) >= maxDurationMs;
    const isBudget = evalCount >= maxEvaluations;
    if (isBudget || isTimeout) {
      budgetExhausted = true;
      unevaluatedRegions.push({
        startUtc: new Date(currentEpochSec * 1000).toISOString(),
        endUtc: new Date(endEpochSec * 1000).toISOString(),
        regionType: 'UNEVALUATED',
        reason: isTimeout ? 'EXECUTION_TIMEOUT_EXCEEDED' : 'EVALUATION_BUDGET_EXHAUSTED'
      });
      break;
    }

    const currentUtcIso = new Date(currentEpochSec * 1000).toISOString();
    evalCount++;
    const currentInvariants = getStructuralInvariantsAtInstant(currentUtcIso, latitude, longitude);

    // 2. Velocity-adaptive step size
    const vel = estimateAscendantVelocity(currentUtcIso, latitude, longitude);
    const step = customStepSeconds || vel.recommendedStepSeconds;
    if (step > 1) {
      samplingUncertainty = true;
    }

    // 3. Step forward to find transition
    let nextEpochSec = Math.min(endEpochSec, currentEpochSec + step);
    let transitionFound = false;

    while (nextEpochSec <= endEpochSec) {
      const isInnerTimeout = (performance.now() - perfStart) >= maxDurationMs;
      const isInnerBudget = evalCount >= maxEvaluations;
      if (isInnerBudget || isInnerTimeout) {
        budgetExhausted = true;
        break;
      }

      const nextUtcIso = new Date(nextEpochSec * 1000).toISOString();
      evalCount++;
      const nextInvariants = getStructuralInvariantsAtInstant(nextUtcIso, latitude, longitude);

      if (!areInvariantsEqual(currentInvariants, nextInvariants)) {
        transitionFound = true;
        break;
      }

      if (nextEpochSec === endEpochSec) {
        break;
      }

      nextEpochSec = Math.min(endEpochSec, nextEpochSec + step);
    }

    if (budgetExhausted) {
      const isTimeoutBreak = (performance.now() - perfStart) >= maxDurationMs;
      unevaluatedRegions.push({
        startUtc: new Date(currentEpochSec * 1000).toISOString(),
        endUtc: new Date(endEpochSec * 1000).toISOString(),
        regionType: 'UNEVALUATED',
        reason: isTimeoutBreak ? 'EXECUTION_TIMEOUT_EXCEEDED' : 'EVALUATION_BUDGET_EXHAUSTED'
      });
      break;
    }

    let plateauEndEpochSec = nextEpochSec;

    if (transitionFound) {
      const prevStepSec = Math.max(currentEpochSec, nextEpochSec - step);
      const bisection = bisectPlateauBoundary(
        new Date(prevStepSec * 1000).toISOString(),
        new Date(nextEpochSec * 1000).toISOString(),
        latitude,
        longitude
      );
      evalCount += bisection.evaluationsExecuted;

      if (!bisection.converged) {
        // P3-01: Bisection non-convergence handling
        if (prevStepSec > currentEpochSec) {
          const verifiedEndMs = prevStepSec * 1000;
          const verifiedStartMs = currentEpochSec * 1000;
          const vStartUtc = new Date(verifiedStartMs).toISOString();
          const vEndUtc = new Date(verifiedEndMs).toISOString();
          const vSpanSec = prevStepSec - currentEpochSec;

          const vStartLocal = formatUtcToLocal(verifiedStartMs, timezoneId).localTime;
          const vEndLocal = formatUtcToLocal(verifiedEndMs, timezoneId).localTime;
          const vIntervalString = `${vStartLocal} - ${vEndLocal}`;
          const vMidMs = (currentEpochSec + Math.floor(vSpanSec / 2)) * 1000;
          const vMidLocal = formatUtcToLocal(vMidMs, timezoneId).localTime;

          let vVerificationLevel: PlateauVerificationLevel = 'SAMPLED_CONSISTENT';
          let vDenseFailed = false;
          if (denseVerify && vSpanSec > 0) {
            const unif = verifyIntervalUniformity(vStartUtc, vEndUtc, latitude, longitude, currentInvariants, 1);
            evalCount += unif.evaluatedSeconds;
            if (unif.isUniform) {
              vVerificationLevel = 'DENSE_VERIFIED';
            } else {
              vVerificationLevel = 'UNRESOLVED';
              vDenseFailed = true;
            }
          }

          if (vDenseFailed) {
            unevaluatedRegions.push({
              startUtc: vStartUtc,
              endUtc: vEndUtc,
              regionType: 'UNEVALUATED',
              reason: 'DENSE_VERIFICATION_DISCREPANCY'
            });
          } else {
            plateaus.push({
              startLocalTime: vStartLocal,
              endLocalTime: vEndLocal,
              intervalString: vIntervalString,
              spanSeconds: vSpanSec,
              mathematicalMidpointReference: vMidLocal,
              invariantsHeld: currentInvariants,
              verificationLevel: vVerificationLevel,
              startUtcInstant: vStartUtc,
              endUtcInstant: vEndUtc
            });

            evaluatedRegions.push({
              startUtc: vStartUtc,
              endUtc: vEndUtc,
              regionType: 'EVALUATED',
              reason: 'ASTROLOGICAL_PLATEAU_EVALUATION',
              stepSizeMinutes: step / 60
            });
          }
        }

        // Bracket [prevStepSec, nextEpochSec) failed convergence -> record in unevaluatedRegions
        unevaluatedRegions.push({
          startUtc: new Date(prevStepSec * 1000).toISOString(),
          endUtc: new Date(nextEpochSec * 1000).toISOString(),
          regionType: 'UNEVALUATED',
          reason: 'BISECTION_CONVERGENCE_FAILURE'
        });

        // Advance to nextEpochSec and continue
        currentEpochSec = nextEpochSec;
        continue;
      }

      plateauEndEpochSec = Math.floor(new Date(bisection.boundaryUtc).getTime() / 1000);
    }

    const pStartMs = currentEpochSec * 1000;
    const pEndMs = plateauEndEpochSec * 1000;
    const pStartUtc = new Date(pStartMs).toISOString();
    const pEndUtc = new Date(pEndMs).toISOString();
    const spanSec = plateauEndEpochSec - currentEpochSec;

    const startLocal = formatUtcToLocal(pStartMs, timezoneId).localTime;
    const endLocal = formatUtcToLocal(pEndMs, timezoneId).localTime;
    const intervalString = `${startLocal} - ${endLocal}`;

    const midEpoch = currentEpochSec + Math.floor(spanSec / 2);
    const midMs = midEpoch * 1000;
    const midLocal = formatUtcToLocal(midMs, timezoneId).localTime;

    let verificationLevel: PlateauVerificationLevel = 'SAMPLED_CONSISTENT';
    let denseFailed = false;
    if (denseVerify && spanSec > 0) {
      const unif = verifyIntervalUniformity(pStartUtc, pEndUtc, latitude, longitude, currentInvariants, 1);
      evalCount += unif.evaluatedSeconds;
      if (unif.isUniform) {
        verificationLevel = 'DENSE_VERIFIED';
      } else {
        verificationLevel = 'UNRESOLVED';
        denseFailed = true;
      }
    }

    if (spanSec > 0) {
      if (denseFailed) {
        // P3-05: Discrepancy logged to unevaluatedRegions, NOT added to plateaus or evaluatedRegions
        unevaluatedRegions.push({
          startUtc: pStartUtc,
          endUtc: pEndUtc,
          regionType: 'UNEVALUATED',
          reason: 'DENSE_VERIFICATION_DISCREPANCY'
        });
      } else {
        plateaus.push({
          startLocalTime: startLocal,
          endLocalTime: endLocal,
          intervalString,
          spanSeconds: spanSec,
          mathematicalMidpointReference: midLocal,
          invariantsHeld: currentInvariants,
          verificationLevel,
          startUtcInstant: pStartUtc,
          endUtcInstant: pEndUtc
        });

        evaluatedRegions.push({
          startUtc: pStartUtc,
          endUtc: pEndUtc,
          regionType: 'EVALUATED',
          reason: 'ASTROLOGICAL_PLATEAU_EVALUATION',
          stepSizeMinutes: step / 60
        });
      }
    }

    // Advance to boundary
    currentEpochSec = plateauEndEpochSec;
  }

  const durationMs = Math.round(performance.now() - perfStart);

  // Coverage Status determination
  let coverageStatus: SearchCoverageStatus = 'EXHAUSTIVE';
  let outcomeStatus: SearchOutcomeStatus = 'SUCCESS_CONVERGENT_INTERVAL';

  if (budgetExhausted || unevaluatedRegions.length > 0) {
    coverageStatus = 'TRUNCATED_BY_BUDGET';
    outcomeStatus = 'ACTION_REQUIRED_PARTIAL_COVERAGE';
  } else if (plateaus.length > 1) {
    outcomeStatus = 'SUCCESS_MULTIPLE_CANDIDATE_CLUSTERS';
  } else if (plateaus.length === 0) {
    outcomeStatus = 'INCONCLUSIVE_INSUFFICIENT_EVIDENCE';
    coverageStatus = 'INSUFFICIENT_DATA';
  }

  // Canonical coverage status calculation enforcing completeness invariants
  const canonicalCoverageStatus = computeSearchCoverageStatus({
    evaluatedRegions,
    prunedRegions: [],
    unevaluatedRegions,
    budgetExceeded: budgetExhausted,
    totalTheoreticalSeconds: totalWindowSeconds
  });
  if (canonicalCoverageStatus !== 'INSUFFICIENT_DATA' || plateaus.length === 0) {
    coverageStatus = canonicalCoverageStatus;
  }

  let evaluatedSecSum = 0;
  for (const r of evaluatedRegions) {
    evaluatedSecSum += Math.round((new Date(r.endUtc).getTime() - new Date(r.startUtc).getTime()) / 1000);
  }
  let unevaluatedSecSum = 0;
  for (const r of unevaluatedRegions) {
    unevaluatedSecSum += Math.round((new Date(r.endUtc).getTime() - new Date(r.startUtc).getTime()) / 1000);
  }

  const completenessRatio = totalWindowSeconds > 0
    ? Math.min(1.0, evaluatedSecSum / totalWindowSeconds)
    : 0;

  const coverageAudit: PlateauCoverageAudit = {
    requestedEnvelope: {
      axisBDateRange: { start: startUtc.split('T')[0], end: endUtc.split('T')[0] },
      axisATimeRange: { start: startUtc.split('T')[1].substring(0, 8), end: endUtc.split('T')[1].substring(0, 8) },
      totalTheoreticalSeconds: totalWindowSeconds
    },
    budgetLimits: {
      maxEvaluationsAllowed: maxEvaluations,
      actualEvaluationsExecuted: evalCount,
      maxTimeMsAllowed: maxDurationMs,
      actualTimeMsTaken: durationMs,
      budgetExceeded: budgetExhausted
    },
    stagesExecuted: [
      {
        stageName: 'MicroPlateau',
        stepMinutes: (customStepSeconds || 60) / 60,
        evaluatedCount: evaluatedRegions.length,
        prunedCount: 0,
        pruningCriteria: 'NONE_EXHAUSTIVE_PLATEAU_SCAN'
      }
    ],
    evaluatedRegions,
    prunedRegions: [],
    unevaluatedRegions,
    coverageStatus,
    completenessRatio,
    samplingUncertainty,
    minimumResolvablePlateauSeconds: customStepSeconds || 60,
    auditTimestamp: new Date().toISOString(),
    engineVersion: BTR_PLATEAU_ENGINE_VERSION
  };

  // Validate ledger integrity via canonical validator
  const auditValidation = validateSearchCoverageAudit(coverageAudit);
  if (!auditValidation.isValid) {
    throw new Error(`SearchCoverageAudit integrity validation failed: ${auditValidation.errors.join(', ')}`);
  }

  return {
    plateaus,
    coverageAudit,
    outcomeStatus,
    evaluationCount: evalCount,
    durationMs
  };
}

